import React, { FC, Suspense } from "react";

import { LiveMesure, useDimensions, FunctionOverrideKey, DataViewKey } from "@keepeek/commons";
import { Box, LinearProgress, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import clsx from "clsx";
import { useRecoilValue } from "recoil";

import MediaDetailHeaderActions from "../../../containers/Element/MediaDetailHeaderActions";
import MediaDetailHeaderNavigation from "../../../containers/Element/MediaDetailHeaderNavigation";
import KViewer from "../../../containers/KViewer";
import KTemplates from "../../../containers/widgets/KTemplates";
import { getCustomerOverrideFunction } from "../../../lib/overrides";
import { Theme } from "../../../models/configuration/theme";
import { backContextState } from "../../../providers/backContext/atoms";
import { useConfiguration } from "../../../providers/config/hooks";
import { useElement } from "../../../providers/element/hooks";
import { CONFIGURATION_SECTION_PATH } from "../../layout/adminMenu";
import MediaDetailHeaderTitleLayout from "../../layout/detail/header/MediaDetailHeaderTitleLayout";
import MediaDetailFixedLayout from "../../layout/detail/modes/MediaDetailFixedLayout";
import MediaDetailHeaderMetaField from "../header/MediaDetailHeaderMetaField";
import MediaDetailHeaderSideMetaField from "../header/MediaDetailHeaderSideMetaField";
import MediaDetailHeaderTechnicalInfo from "../header/MediaDetailHeaderTechnicalInfo";
import { SpecificMediaDetailProps } from "./MediaDetailMode";

const MediaDetailFixed: FC<React.PropsWithChildren<SpecificMediaDetailProps>> =
  function MediaDetailFixed({ className, templatesConfiguration, widgetsConfiguration }) {
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.down("lg"));
    const themeConfiguration = useConfiguration<Theme>(CONFIGURATION_SECTION_PATH.THEME);
    const backContext = useRecoilValue(backContextState);

    const {
      element,
      fields,
      showAttachments,
      showAddToBasket,
      previewDimensions,
      showUnitaryPublicShare,
      isUnitaryShareElement,
    } = useElement();
    const { ref, dimensions } = useDimensions({ liveMeasure: LiveMesure.Resize });

    // Loading state
    if (!element) {
      return (
        <MediaDetailFixedLayout
          className={clsx("MediaDetailFixed", "MediaDetailFixed-loading", className)}
          navigation={!isUnitaryShareElement && <MediaDetailHeaderNavigation />}
          actions={<Skeleton height="54px" />}
          viewer={<Skeleton height="100%" width="100%" />}
          title={
            <MediaDetailHeaderTitleLayout
              title={<Skeleton width="50%" />}
              subtitle={<Skeleton width="75%" />}
              sideInfo={fields && fields.headerSideField && <Skeleton width="35%" />}
              technicalInfo={
                fields &&
                Array.isArray(fields.headerTechnicalFields) &&
                fields.headerTechnicalFields.length > 0 && <Skeleton width="40%" />
              }
            />
          }
          kTemplates={<Box height="50vh" />}
        />
      );
    }

    const viewerMode = (): "center" | "top" => {
      if (matches) {
        return "center";
      }
      if (
        dimensions.width &&
        dimensions.width !== 0 &&
        previewDimensions.width &&
        previewDimensions.width !== 0
      ) {
        return dimensions.width > previewDimensions.width ? "center" : "top";
      }
      return "top";
    };

    const headerFunctionalFields =
      getCustomerOverrideFunction<FunctionOverrideKey.FunctionalFieldsToDisplayQueryOut>(
        FunctionOverrideKey.FunctionalFieldsToDisplayQueryOut,
        {
          element: element,
          functionalFieldsToDisplayBeforeCustomerHook: fields?.headerFunctionalFields || [],
        },
      ) || fields?.headerFunctionalFields;

    return (
      <MediaDetailFixedLayout
        className={clsx("MediaDetailFixed", className)}
        viewer={
          <Suspense fallback={<LinearProgress />}>
            <KViewer mode={viewerMode()} media={element} ref={ref} />
          </Suspense>
        }
        navigation={!isUnitaryShareElement && <MediaDetailHeaderNavigation />}
        actions={
          <MediaDetailHeaderActions
            kpkMedia={element}
            showAttachments={showAttachments}
            showAddToBasket={showAddToBasket}
            showUnitaryPublicShare={showUnitaryPublicShare}
          />
        }
        title={
          <MediaDetailHeaderTitleLayout
            title={
              <Typography
                variant="h6"
                component="h1"
                sx={{ overflowWrap: "break-word" }}
                title={typeof element.title.value === "string" ? element.title.value : ""}
              >
                {element.title.value}
              </Typography>
            }
            subtitle={
              fields &&
              Array.isArray(headerFunctionalFields) &&
              headerFunctionalFields.length > 0 && (
                <MediaDetailHeaderMetaField media={element} fields={headerFunctionalFields} />
              )
            }
            sideInfo={
              fields &&
              fields.headerSideField && (
                <MediaDetailHeaderSideMetaField
                  media={element}
                  headerSideField={fields.headerSideField}
                />
              )
            }
            technicalInfo={
              fields &&
              Array.isArray(fields.headerTechnicalFields) &&
              fields.headerTechnicalFields.length > 0 && (
                <MediaDetailHeaderTechnicalInfo
                  element={element}
                  headerTechnicalFields={fields.headerTechnicalFields}
                />
              )
            }
          />
        }
        kTemplates={
          <KTemplates
            templatesConfiguration={templatesConfiguration}
            widgetsConfiguration={widgetsConfiguration}
            themeConfiguration={themeConfiguration || null}
            dataViewKey={backContext.key || DataViewKey.SEARCH}
          />
        }
      />
    );
  };

export default MediaDetailFixed;
